/**
 * Import from statement (owner, 15 Oct 2026): the adviser drops a client's documents — PDFs, photos,
 * spreadsheets — and gets draft rows back to tick, grouped by file: the person's assets, the assets
 * a trust or company holds (which land under that entity, created if the profile has none of that
 * name), and liabilities. Nothing lands on the profile until they confirm. Rendered through a portal
 * so it opens centred on the screen whatever the page's scroll (it used to land at the bottom).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, ShieldCheck, AlertTriangle, Building2, Upload, Sparkles, FileSpreadsheet, Image as ImageIcon, Landmark } from 'lucide-react';
import { Button, IconTile } from '../ui/index.tsx';
import { extractAssetsFromStatements, statementImportFailureForUser, type StatementImportResult } from '../../services/statementImportApi.ts';
import {
    combineHoldings, importedTypeLabel, importedLiabilityTypeLabel,
    type StatementImportDraft, type ImportedLiabilityDraft,
} from '../../../../shared/src/calculation/statementImport.ts';
import { formatCurrency } from '../../utils/index.ts';

const confidenceClass: Record<StatementImportDraft['confidence'], string> = {
    high: 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100',
    medium: 'bg-blue-50 text-blue-800 ring-1 ring-blue-100',
    low: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
};

type ImportPhase = 'upload' | 'processing' | 'review';

export const ACCEPTED_FILES = '.pdf,.xlsx,.xls,.csv,image/*';
export const PRIVACY_LINE = 'Your file is read by an AI service to draft these entries and is not stored. Cover or leave out anything with a tax file number on it.';

export interface StatementImportPick {
    assets: StatementImportDraft[];
    liabilities: ImportedLiabilityDraft[];
}

interface AssetStatementImportModalProps {
    onClose: () => void;
    onConfirm: (picked: StatementImportPick) => void;
    household: { isCouple: boolean; clientName?: string | null; partnerName?: string | null };
    existingAssets?: Record<string, unknown>;
    existingHome?: { value?: unknown } | null;
    /** Overridable for tests. */
    extract?: typeof extractAssetsFromStatements;
}

const AssetStatementImportModal = ({ onClose, onConfirm, household, existingAssets, existingHome = null, extract = extractAssetsFromStatements }: AssetStatementImportModalProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [phase, setPhase] = useState<ImportPhase>('upload');
    const [files, setFiles] = useState<File[]>([]);
    const [drafts, setDrafts] = useState<StatementImportDraft[]>([]);
    const [liabilities, setLiabilities] = useState<ImportedLiabilityDraft[]>([]);
    const [combine, setCombine] = useState<Record<string, boolean>>({});
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    // While the reader works, the words move so the wait reads as progress rather than a hang.
    const READING_STEPS = ['Opening the documents…', 'Reading each page…', 'Finding the assets and loans…', 'Matching the fields to your profile…', 'Nearly there…'];
    const [readingStep, setReadingStep] = useState(0);
    useEffect(() => {
        if (phase !== 'processing') { setReadingStep(0); return undefined; }
        const t = setInterval(() => setReadingStep((i) => Math.min(i + 1, READING_STEPS.length - 1)), 7_000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const runExtraction = async (selectedFiles: File[]) => {
        if (!selectedFiles.length) { setError('Choose at least one file.'); return; }
        if (selectedFiles.length > 5) { setError('Up to 5 files at a time.'); return; }
        setError('');
        setPhase('processing');
        try {
            const result: StatementImportResult = await extract({ files: selectedFiles, household, existingAssets, existingHome });
            setDrafts(result.assets);
            setLiabilities(result.liabilities);
            setCombine({});
            setPhase('review');
            if (!result.assets.length && !result.liabilities.length) setError('Nothing that looks like an asset or a loan was found in those files. Try a clearer copy, or add them by hand.');
        } catch (err) {
            setError(statementImportFailureForUser(err));
            setPhase('upload');
        }
    };

    const handleFiles = (fileList: FileList | null) => {
        const next = Array.from(fileList || []);
        setFiles(next);
        if (next.length) runExtraction(next);
    };

    const addable = (row: StatementImportDraft) => !!row.assetTypeId || row.kind === 'home';
    const toggleRow = (id: string) => setDrafts((prev) => prev.map((row) => (row.id === id && addable(row) ? { ...row, selected: !row.selected } : row)));
    const toggleLiability = (id: string) => setLiabilities((prev) => prev.map((row) => (row.id === id && row.liabilityTypeId ? { ...row, selected: !row.selected } : row)));
    const setFileSelection = (file: string, selected: boolean) => {
        setDrafts((prev) => prev.map((row) => (row.sourceFile === file && addable(row) ? { ...row, selected } : row)));
        setLiabilities((prev) => prev.map((row) => (row.sourceFile === file && row.liabilityTypeId ? { ...row, selected } : row)));
    };

    /** Rows by the file they came from, in upload order: the person's own, then each entity's, then the loans. */
    const groups = useMemo(() => {
        const files = Array.from(new Set([...drafts.map((d) => d.sourceFile), ...liabilities.map((d) => d.sourceFile)]));
        return files.map((file) => {
            const rows = drafts.filter((d) => d.sourceFile === file);
            const own = rows.filter((d) => !d.entity);
            const byEntity = new Map<string, StatementImportDraft[]>();
            rows.filter((d) => d.entity).forEach((d) => byEntity.set(d.entity!.name, [...(byEntity.get(d.entity!.name) || []), d]));
            return { file, own, entities: Array.from(byEntity.entries()), loans: liabilities.filter((d) => d.sourceFile === file), combined: combineHoldings(own) };
        });
    }, [drafts, liabilities]);

    /** What confirm adds: each file's ticked rows, or its one combined line where the adviser chose that. */
    const toAdd = useMemo<StatementImportPick>(() => ({
        assets: groups.flatMap((g) => [
            ...(combine[g.file] && g.combined ? [g.combined, ...g.own.filter((d) => d.selected && d.kind === 'home')] : g.own.filter((d) => d.selected && addable(d))),
            ...g.entities.flatMap(([, rows]) => rows.filter((d) => d.selected && d.assetTypeId)),
        ]),
        liabilities: liabilities.filter((d) => d.selected && d.liabilityTypeId),
    }), [groups, combine, liabilities]);
    const toAddCount = toAdd.assets.length + toAdd.liabilities.length;

    const handleConfirm = () => {
        if (!toAddCount) { setError('Select at least one item to add.'); return; }
        onConfirm(toAdd);
    };

    const ownerLabel = (ownership: string) => (ownership === 'joint' ? 'joint' : ownership === 'partner' ? (household.partnerName || 'partner') : (household.clientName || 'client'));

    const fieldsSummary = (row: StatementImportDraft) => {
        const bits: string[] = [];
        const f = row.fields as Record<string, unknown>;
        if (f.income != null) bits.push(`${formatCurrency(Number(f.income))} a year`);
        if (f.commencementDate) bits.push(`started ${String(f.commencementDate)}`);
        if (f.purchasePrice != null) bits.push(`bought for ${formatCurrency(Number(f.purchasePrice))}`);
        if (f.costBase != null) bits.push(`cost base ${formatCurrency(Number(f.costBase))}`);
        if (f.acquiredOn) bits.push(`acquired ${String(f.acquiredOn)}`);
        if (f.maturityDate) bits.push(`matures ${String(f.maturityDate)}`);
        if (f.incomeRate != null) bits.push(`${Number(f.incomeRate)}% p.a.`);
        if (f.taxFree != null) bits.push(`tax-free part ${formatCurrency(Number(f.taxFree))}`);
        if (f.rcv != null) bits.push(`residual ${formatCurrency(Number(f.rcv))}`);
        return bits.join(' · ');
    };
    const loanSummary = (row: ImportedLiabilityDraft) => {
        const bits: string[] = [];
        const f = row.fields;
        if (f.interestRate != null) bits.push(`${f.interestRate}% p.a.`);
        if (f.repayments != null) bits.push(`${formatCurrency(Number(f.repayments))} ${f.frequency || 'monthly'}`);
        if (f.loanType) bits.push(f.loanType === 'io' ? 'interest only' : 'principal and interest');
        if (f.loanTerm != null) bits.push(`${f.loanTerm} years left`);
        return bits.join(' · ');
    };

    const assetRow = (row: StatementImportDraft, disabled: boolean) => (
        <li key={row.id} className={`flex items-start gap-3 px-3 py-2.5 ${!addable(row) ? 'bg-slate-50/80' : ''}`}>
            <input type="checkbox" className="mt-1" checked={row.selected && !(disabled && row.kind !== 'home')} disabled={!addable(row) || (disabled && row.kind !== 'home')} onChange={() => toggleRow(row.id)} aria-label={`Select ${row.description}`} />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900 truncate">{row.description}</p>
                    <p className="text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">{row.value ? formatCurrency(Number(row.value)) : '—'}</p>
                </div>
                <p className="text-xs text-slate-500">
                    {importedTypeLabel(row.assetTypeId, row.kind)}
                    {row.assetTypeId && !row.entity && <> · {ownerLabel(row.ownership)}</>}
                    {row.fieldsAsked > 0 && <> · {row.fieldsFound} of {row.fieldsAsked} fields found</>}
                    <span className={`ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${confidenceClass[row.confidence]}`}>{row.confidence}</span>
                </p>
                {fieldsSummary(row) && <p className="text-xs text-slate-600 mt-0.5">{fieldsSummary(row)}</p>}
                {(row.flags.length > 0 || row.notes) && (
                    <p className="text-xs text-slate-600 mt-0.5 flex items-start gap-1">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-500" aria-hidden />
                        <span>{[...row.flags, row.notes].filter(Boolean).join(' · ')}</span>
                    </p>
                )}
            </div>
        </li>
    );

    const body = (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                role="dialog"
                aria-modal="true"
                aria-labelledby="statement-import-title"
                onClick={(e) => e.stopPropagation()}
                data-testid="statement-import-modal"
            >
                <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <IconTile icon={Sparkles} tone="brand" variant="solid" size="md" />
                        <div className="min-w-0">
                            <h2 id="statement-import-title" className="text-lg font-bold text-slate-900 leading-tight">Import from a statement</h2>
                            <p className="text-sm text-slate-500 mt-0.5">
                                {phase === 'review' ? 'Tick what to add — each row lands as a card you can edit.' : 'Drop the documents and we draft the entries for you to check.'}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 -mr-1 hover:bg-slate-100 rounded-full transition-colors" aria-label="Close">
                        <X size={20} className="text-slate-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {phase === 'upload' && (
                        <>
                            <div
                                className={`relative overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-200 ${dragOver ? 'border-blue-500 bg-blue-50/60 scale-[1.01]' : 'border-slate-200 bg-gradient-to-b from-slate-50 to-white hover:border-blue-300'}`}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                                data-testid="import-dropzone"
                            >
                                <div className="px-6 py-10 text-center">
                                    <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-teal-400 text-white shadow-lg shadow-blue-500/25 flex items-center justify-center">
                                        <Upload size={28} strokeWidth={2.2} aria-hidden />
                                    </div>
                                    <p className="text-base font-bold text-slate-900">Drop your statements here</p>
                                    <p className="mt-1 text-sm text-slate-500">Super, pension, bank, share and loan statements, a Centrelink schedule, a platform report, or a spreadsheet of holdings.</p>
                                    <div className="mt-5">
                                        <Button variant="brand" size="md" icon={FileText} onClick={() => fileInputRef.current?.click()}>Choose files</Button>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={ACCEPTED_FILES}
                                        multiple
                                        className="hidden"
                                        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                                    />
                                    <div className="mt-5 flex flex-wrap justify-center gap-2">
                                        {[
                                            { Icon: FileText, label: 'PDF' },
                                            { Icon: ImageIcon, label: 'Photo' },
                                            { Icon: FileSpreadsheet, label: 'Excel or CSV' },
                                            { Icon: Landmark, label: 'Up to 5 files · 8 MB each' },
                                        ].map(({ Icon, label }) => (
                                            <span key={label} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                                <Icon size={12} className="text-slate-400" aria-hidden />{label}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 flex items-start gap-3" data-testid="import-disclaimer">
                                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" aria-hidden />
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    <strong className="font-semibold text-slate-900">AI can make mistakes.</strong> Check every figure against the document before you rely on it — nothing is added to the profile until you tick it, and you can edit anything afterwards.
                                </p>
                            </div>
                            {files.length > 0 && <p className="text-xs text-slate-500 mt-3">Selected: {files.map((f) => f.name).join(', ')}</p>}
                        </>
                    )}

                    {phase === 'processing' && (
                        <div className="py-10 flex flex-col items-center text-center" data-testid="import-reading">
                            <div className="relative mb-5 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-teal-400 text-white shadow-lg shadow-blue-500/25 flex items-center justify-center">
                                <Sparkles size={28} strokeWidth={2.2} aria-hidden className="animate-pulse" />
                            </div>
                            <p className="text-base font-bold text-slate-900">Reading {files.length === 1 ? 'your statement' : `${files.length} files`}</p>
                            <p className="mt-1 text-sm text-slate-600" aria-live="polite">{READING_STEPS[readingStep]}</p>
                            <div className="mt-5 w-full max-w-xs h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-blue-500 to-teal-400 animate-[importSweep_1.4s_ease-in-out_infinite]" />
                            </div>
                            <p className="mt-3 text-xs text-slate-400">A page or two takes about 10 seconds; a long schedule can take a minute.</p>
                            <style>{`@keyframes importSweep { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }`}</style>
                        </div>
                    )}

                    {phase === 'review' && (
                        <>
                            <p className="text-sm text-slate-600 mb-3">Anything a trust or company holds lands under that entity, created if it is not on the profile yet.</p>
                            <div className="space-y-4">
                                {groups.map((g) => {
                                    const selectable = [...g.own, ...g.entities.flatMap(([, r]) => r)].filter(addable).length + g.loans.filter((d) => d.liabilityTypeId).length;
                                    const allOn = selectable > 0
                                        && [...g.own, ...g.entities.flatMap(([, r]) => r)].filter(addable).every((d) => d.selected)
                                        && g.loans.filter((d) => d.liabilityTypeId).every((d) => d.selected);
                                    return (
                                        <div key={g.file} className="rounded-xl border border-slate-200" data-testid="import-file-group">
                                            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 rounded-t-xl border-b border-slate-200">
                                                <p className="text-xs font-semibold text-slate-700 truncate"><FileText size={13} className="inline -mt-0.5 mr-1 text-slate-400" aria-hidden />{g.file}</p>
                                                {selectable > 1 && (
                                                    <label className="text-xs text-slate-600 flex items-center gap-1.5 whitespace-nowrap">
                                                        <input type="checkbox" checked={allOn} onChange={(e) => setFileSelection(g.file, e.target.checked)} />
                                                        Select all ({selectable})
                                                    </label>
                                                )}
                                            </div>
                                            {g.own.length > 0 && <ul className="divide-y divide-slate-100">{g.own.map((row) => assetRow(row, !!combine[g.file]))}</ul>}
                                            {g.combined && (
                                                <label className="flex items-start gap-2 px-3 py-2.5 border-t border-slate-200 text-xs text-slate-700">
                                                    <input type="checkbox" className="mt-0.5" checked={!!combine[g.file]} onChange={(e) => setCombine((c) => ({ ...c, [g.file]: e.target.checked }))} />
                                                    <span>
                                                        Add the ticked holdings as <strong>one combined line</strong> ({formatCurrency(Number(g.combined.value))}
                                                        {g.combined.fields.costBase != null ? <>, cost base {formatCurrency(Number(g.combined.fields.costBase))}</> : null}) instead of {g.own.filter((d) => d.selected && d.assetTypeId).length} separate ones.
                                                    </span>
                                                </label>
                                            )}
                                            {g.entities.map(([name, rows]) => (
                                                <div key={name} data-testid="import-entity-group">
                                                    <p className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border-y border-slate-200 text-[11px] font-semibold tracking-wide text-slate-600 uppercase">
                                                        <Building2 size={12} className="text-slate-400" aria-hidden /> Held by {name}
                                                    </p>
                                                    <ul className="divide-y divide-slate-100">{rows.map((row) => assetRow(row, false))}</ul>
                                                </div>
                                            ))}
                                            {g.loans.length > 0 && (
                                                <div data-testid="import-liability-group">
                                                    <p className="px-3 py-1.5 bg-slate-50 border-y border-slate-200 text-[11px] font-semibold tracking-wide text-slate-600 uppercase">Loans and debts</p>
                                                    <ul className="divide-y divide-slate-100">
                                                        {g.loans.map((row) => (
                                                            <li key={row.id} className={`flex items-start gap-3 px-3 py-2.5 ${!row.liabilityTypeId ? 'bg-slate-50/80' : ''}`}>
                                                                <input type="checkbox" className="mt-1" checked={row.selected} disabled={!row.liabilityTypeId} onChange={() => toggleLiability(row.id)} aria-label={`Select ${row.description}`} />
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-baseline justify-between gap-3">
                                                                        <p className="text-sm font-semibold text-slate-900 truncate">{row.description}</p>
                                                                        <p className="text-sm font-semibold text-rose-700 tabular-nums whitespace-nowrap">{row.balance ? `−${formatCurrency(Number(row.balance))}` : '—'}</p>
                                                                    </div>
                                                                    <p className="text-xs text-slate-500">
                                                                        {importedLiabilityTypeLabel(row.liabilityTypeId)}
                                                                        {row.liabilityTypeId && <> · {row.entity ? `owed by ${row.entity.name}` : ownerLabel(row.ownership)}</>}
                                                                        <span className={`ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${confidenceClass[row.confidence]}`}>{row.confidence}</span>
                                                                    </p>
                                                                    {loanSummary(row) && <p className="text-xs text-slate-600 mt-0.5">{loanSummary(row)}</p>}
                                                                    {(row.flags.length > 0 || row.notes) && (
                                                                        <p className="text-xs text-slate-600 mt-0.5 flex items-start gap-1">
                                                                            <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-500" aria-hidden />
                                                                            <span>{[...row.flags, row.notes].filter(Boolean).join(' · ')}</span>
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-5">
                                <Button variant="brand" size="md" disabled={toAddCount === 0} onClick={handleConfirm}>
                                    Add {toAddCount} to the profile
                                </Button>
                                <Button variant="secondary" size="md" onClick={() => { setDrafts([]); setLiabilities([]); setFiles([]); setCombine({}); setError(''); setPhase('upload'); }}>
                                    Upload another
                                </Button>
                            </div>
                        </>
                    )}

                    {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}

                </div>
                <div className="shrink-0 px-6 py-3 border-t border-slate-100 bg-slate-50/70 rounded-b-2xl flex items-start gap-2 text-[11px] text-slate-500">
                    <ShieldCheck size={14} className="mt-px shrink-0 text-emerald-600" aria-hidden />
                    <span>{PRIVACY_LINE}</span>
                </div>
            </div>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(body, document.body) : body;
};

export default AssetStatementImportModal;
